<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import SectionCard from '../SectionCard.vue';
import ImageUpload from '../components/ImageUpload.vue';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { emptyToNull } from '../helpers';
import {
  apiErrorMessage,
  iconErrorMessage,
} from '../../../../api/realEstate/errors';

// No modal — nearby amenities are added/removed inline, matching the
// prototype. `distance_km` is server-computed now (was a local km()
// helper call); an icon is a separate upload, same reasoning as Features.
export default {
  name: 'NearbySection',
  components: { SectionCard, ImageUpload },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      form: {
        name: '',
        distance_value: null,
        distance_unit: 'km',
        maps_url: '',
      },
    };
  },
  methods: {
    ...mapActions('realEstate', [
      'addNearby',
      'removeNearby',
      'setNearbyIconFile',
      'removeNearbyIcon',
    ]),
    async handleAdd() {
      const name = this.form.name.trim();
      const distanceValue = emptyToNull(this.form.distance_value);
      if (!name || distanceValue == null) return;
      try {
        await this.addNearby({
          propertyId: this.project.property_id,
          fields: {
            name,
            distance_value: distanceValue,
            distance_unit: this.form.distance_unit,
            maps_url: this.form.maps_url.trim() || null,
          },
        });
        this.form = {
          name: '',
          distance_value: null,
          distance_unit: 'km',
          maps_url: '',
        };
        useAlert(this.$t('REAL_ESTATE.NEARBY.SUCCESS_ADDED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.NEARBY.ERROR_ADD'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleRemove(place) {
      try {
        await this.removeNearby({
          propertyId: this.project.property_id,
          nearbyId: place.amenity_id,
        });
        useAlert(this.$t('REAL_ESTATE.NEARBY.SUCCESS_REMOVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.NEARBY.ERROR_REMOVE'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleIconSelect(place, file) {
      try {
        await this.setNearbyIconFile({
          propertyId: this.project.property_id,
          nearbyId: place.amenity_id,
          file,
        });
        useAlert(this.$t('REAL_ESTATE.NEARBY.SUCCESS_ICON_UPDATED'));
      } catch (err) {
        useAlert(iconErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleIconRemove(place) {
      try {
        await this.removeNearbyIcon({
          propertyId: this.project.property_id,
          nearbyId: place.amenity_id,
        });
        useAlert(this.$t('REAL_ESTATE.NEARBY.SUCCESS_ICON_REMOVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
  },
};
</script>

<template>
  <SectionCard section-id="nearby" :title="$t('REAL_ESTATE.NEARBY.TITLE')">
    <ul v-if="project.nearby.length" class="space-y-3 mb-4">
      <li
        v-for="place in project.nearby"
        :key="place.amenity_id"
        class="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200"
      >
        <ImageUpload
          compact
          allow-svg
          :preview-url="place.icon_url || ''"
          :label="$t('REAL_ESTATE.NEARBY.ICON_BUTTON')"
          @select="file => handleIconSelect(place, file)"
          @remove="handleIconRemove(place)"
        />
        <span class="flex-1 flex items-center gap-2">
          <fluent-icon v-if="!place.icon_url" icon="location" size="16" />
          {{ place.name }} — {{ place.distance_value }}
          {{ place.distance_unit }}
          <span v-if="place.distance_unit !== 'km'" class="text-slate-400">
            {{
              $t('REAL_ESTATE.NEARBY.DISTANCE_KM_SUFFIX', {
                distance: place.distance_km,
              })
            }}
          </span>
        </span>
        <a
          v-if="place.maps_url"
          :href="place.maps_url"
          target="_blank"
          rel="noopener noreferrer"
          class="text-woot-500"
        >
          <fluent-icon icon="open" size="14" />
        </a>
        <woot-button
          variant="clear"
          size="small"
          color-scheme="alert"
          icon="delete"
          @click="handleRemove(place)"
        />
      </li>
    </ul>
    <p v-else class="text-sm text-slate-400 mb-4">
      {{ $t('REAL_ESTATE.NEARBY.EMPTY') }}
    </p>

    <form
      class="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200 dark:border-slate-700"
      @submit.prevent="handleAdd"
    >
      <input
        v-model="form.name"
        :placeholder="$t('REAL_ESTATE.NEARBY.FIELD_NAME_PLACEHOLDER')"
        :class="fieldClass"
        class="flex-1 min-w-[140px] mb-0 reset-base"
        required
      />
      <input
        v-model.number="form.distance_value"
        type="number"
        step="any"
        :placeholder="$t('REAL_ESTATE.NEARBY.FIELD_DISTANCE_PLACEHOLDER')"
        :class="fieldClass"
        class="!w-28 shrink-0 mb-0 reset-base"
        required
      />
      <select
        v-model="form.distance_unit"
        :class="selectClass"
        class="!w-20 shrink-0"
      >
        <option value="km">{{ $t('REAL_ESTATE.NEARBY.UNIT_KM') }}</option>
        <option value="mi">{{ $t('REAL_ESTATE.NEARBY.UNIT_MI') }}</option>
      </select>
      <input
        v-model="form.maps_url"
        :placeholder="$t('REAL_ESTATE.NEARBY.FIELD_MAPS_PLACEHOLDER')"
        :class="fieldClass"
        class="flex-1 min-w-[140px] mb-0 reset-base"
      />
      <woot-button type="submit" variant="smooth" class="shrink-0">
        {{ $t('REAL_ESTATE.NEARBY.ADD_BUTTON') }}
      </woot-button>
    </form>
  </SectionCard>
</template>
